import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

const StatCard = ({ label, value, sub, subColor, icon }) => (
  <div style={{ background:'white', borderRadius:14, padding:'20px 24px', border:'1px solid #E8E4DF', boxShadow:'0 1px 3px rgba(44,44,53,.06)' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#888899', textTransform:'uppercase', letterSpacing:1.5 }}>{label}</div>
      <span style={{ fontSize:22 }}>{icon}</span>
    </div>
    <div style={{ fontFamily:'Playfair Display,serif', fontSize:32, fontWeight:700, color:'#2C2C35', marginBottom:4 }}>{value}</div>
    <div style={{ fontSize:12, fontWeight:600, color: subColor || '#7A9E8E' }}>{sub}</div>
  </div>
);

export default function Dashboard({ onNav }) {
  const [stats,   setStats]   = useState(null);
  const [topBooks, setTop]    = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([adminAPI.getStats(), adminAPI.getActivity()]);
        setStats(s.data);
        setActivity(a.data?.dailyActivity || []);
        const t = await adminAPI.getTopBooks();
        setTop(t.data?.topAnnotated || []);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:36, height:36, border:'3px solid #E8E4DF', borderTopColor:'#C97B77', borderRadius:'50%', animation:'spin .8s linear infinite', margin:'0 auto 12px' }}/>
        <div style={{ fontSize:13, color:'#888899' }}>Loading dashboard…</div>
      </div>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ padding:32, fontFamily:'Nunito,sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:'Playfair Display,serif', fontSize:28, fontWeight:700, color:'#2C2C35' }}>
          Admin <em style={{ color:'#C97B77', fontStyle:'italic' }}>Dashboard</em>
        </div>
        <div style={{ fontSize:13, color:'#888899', marginTop:4 }}>All India Reporter eBook Platform</div>
      </div>

      {/* Stats grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16, marginBottom:32 }}>
        <StatCard label="Total Books"     value={stats?.totalBooks     || 0} sub={`${stats?.enabledBooks || 0} enabled`}  subColor="#7A9E8E" icon="📚"/>
        <StatCard label="Disabled Books"  value={stats?.disabledBooks  || 0} sub="Hidden from users"                      subColor="#C4975A" icon="🚫"/>
        <StatCard label="Total Users"     value={stats?.totalUsers     || 0} sub={`${stats?.activeUsers || 0} active (7d)`} subColor="#6B7FA0" icon="👥"/>
        <StatCard label="Annotations"     value={stats?.totalAnnotations || 0} sub="Highlights + notes"                  subColor="#9B8EC4" icon="✏️"/>
      </div>

      {/* Quick actions */}
      <div style={{ marginBottom:32 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#888899', textTransform:'uppercase', letterSpacing:2, marginBottom:12 }}>Quick Actions</div>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          {[
            { label:'📚 Manage Books',    action: () => onNav('books'),     bg:'#C97B77', color:'white' },
            { label:'👥 View Users',      action: () => onNav('users'),     bg:'#EEF2F8', color:'#6B7FA0' },
            { label:'📈 Analytics',       action: () => onNav('analytics'), bg:'#EBF4F0', color:'#7A9E8E' },
          ].map(a => (
            <button key={a.label} onClick={a.action}
              style={{ padding:'10px 20px', borderRadius:10, background:a.bg, color:a.color, border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Recent activity */}
        <div style={{ background:'white', borderRadius:14, border:'1px solid #E8E4DF', padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#2C2C35', marginBottom:16 }}>📅 Annotation Activity (7 days)</div>
          {activity.length === 0 ? (
            <div style={{ fontSize:12, color:'#888899', textAlign:'center', padding:20 }}>No activity data yet</div>
          ) : activity.map((d, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={{ fontSize:11, color:'#888899', minWidth:80, fontFamily:'monospace' }}>{d._id}</div>
              <div style={{ flex:1, height:8, background:'#F5F2EE', borderRadius:4, overflow:'hidden' }}>
                <div style={{ height:'100%', borderRadius:4, background:'#C97B77', width:`${Math.min(100, d.count * 10)}%` }}/>
              </div>
              <div style={{ fontSize:11, fontWeight:700, color:'#2C2C35', minWidth:24, textAlign:'right' }}>{d.count}</div>
            </div>
          ))}
        </div>

        {/* Top annotated books */}
        <div style={{ background:'white', borderRadius:14, border:'1px solid #E8E4DF', padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#2C2C35', marginBottom:16 }}>🔥 Most Highlighted Books</div>
          {topBooks.length === 0 ? (
            <div style={{ fontSize:12, color:'#888899', textAlign:'center', padding:20 }}>No annotation data yet</div>
          ) : topBooks.slice(0, 6).map((b, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #F0ECE8' }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:'#EEF2F8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#6B7FA0', flexShrink:0 }}>{i+1}</div>
              <div style={{ flex:1, fontSize:12, color:'#2C2C35', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{decrypt(b.bookName) || `Book #${b._id}`}</div>
              <div style={{ fontSize:11, fontWeight:700, color:'#C97B77' }}>{b.count} annotations</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}